import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, Redirect } from 'react-router-dom'
import qs from 'query-string'
import { selectTaskSetting, selectTaskStepSettings } from 'state/constants'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import {
    TaskState,
    registerTask,
    updateTask,
    deleteTask,
    selectTasks,
    updateTaskPayouts,
    updateTaskSchedules,
} from 'state/tasks'
import { SmallScreenSideBar } from '../EditTask/styles'
import RegistrationEditor from './RegistrationEditor'
import CustomTaskEditor from './CustomTaskEditor'
import PredefinedTaskEditor from './PredefinedTaskEditor'
import BaseEmptyState from 'components/Shared/EmptyState'
import TaskSidebar from './TaskSidebar'
import Icon from 'components/Icon/Icon'
import { selectCampaign, State } from 'state'
import { useSelectedTask, withSelectedTaskProvider } from './TaskEditorContext'
import RectangleButton from 'particles/Button/RectangleButton'
import { useSetIdentifierRoute } from 'hooks/comment'
import { getForm } from 'state/registrations'

export enum EditingState {
    BLANK = 0,
    ADD_TASK = 1,
    REGISTRATION_PHASE = 2,
    EDIT_STEPS = 10,
}

export enum TaskSections {
    SETUP = 'setup',
    STEPS = 'steps',
    SCHEDULE = 'schedule',
    PAYOUT = 'payout',
}

const EmptyState: React.FC = () => {
    const [t] = useTranslation()

    return (
        <BaseEmptyState
            className="empty-state"
            width="100%"
            title={t('emptyState.noTasksAddedTitle')}
            body={t('emptyState.addTaskForCampaignSubtitle')}
            titleColor="primary"
            bodyColor="secondary"
        />
    )
}

interface TaskEditorProps {
    uuid: string
}

const TaskEditor: React.FC<TaskEditorProps> = ({ uuid }) => {
    const onAddTaskRef = useRef(false)
    const [t] = useTranslation()
    const location = useLocation()
    const params = qs.parse(location.search)
    const { taskUuid, section } = params
    const dispatch = useDispatch()
    const campaign = useSelector(selectCampaign(uuid))
    const tasks = useSelector(selectTasks)
    const [selectedTask, setSelectedTask] = useSelectedTask()
    const [editingState, setEditingState] = useState<EditingState>(EditingState.BLANK)
    const [selectedTaskSection, setSelectedTaskSection] = useState<TaskSections>(TaskSections.SETUP)
    const [showSidebarTask, setShowSidebarTask] = useState(false)
    const [updateRoute] = useSetIdentifierRoute()

    const selectedTaskSetting = useSelector((state: State) =>
        selectTaskSetting(state, { taskType: selectedTask ? selectedTask.type : undefined }),
    )
    const stepSettings = useSelector((state: State) =>
        selectTaskStepSettings(state, { task: selectedTask || undefined }),
    )

    const selectTask = (task: TaskState | null, setCurrentEditState: EditingState = EditingState.BLANK): void => {
        setSelectedTask(task)
        setEditingState(setCurrentEditState)
    }
    const selectRegistrationPhase = (): void => {
        selectTask(null, EditingState.REGISTRATION_PHASE)
    }
    const campaignBrandCountry = campaign && campaign.brand && campaign.brand.country

    const createTask = (taskName: string): void => {
        if (!uuid) {
            return
        }
        dispatch(
            registerTask({
                taskType: taskName,
                campaignUuid: uuid,
            }),
        )
        onAddTaskRef.current = true
    }

    const removeTask = (taskUuid: string): void => {
        if (!uuid) return
        if (selectedTask && selectedTask.uuid === taskUuid) {
            setEditingState(EditingState.BLANK)
        }
        dispatch(
            deleteTask({
                taskUuid,
                campaignUuid: uuid,
            }),
        )
    }

    const saveTask = (
        task: TaskState,
        idle_hours_to_submit_engagement?: number,
        successCallback?: () => void,
    ): void => {
        if (!uuid) return
        if (
            CAMPAIGN_STATUS_ENUM.LIVE === campaign.status &&
            [TaskSections.SCHEDULE, TaskSections.PAYOUT].includes(selectedTaskSection)
        ) {
            if (selectedTaskSection === TaskSections.PAYOUT) {
                dispatch(
                    updateTaskPayouts({
                        taskUuid: task.uuid,
                        payouts: task.payouts,
                    }),
                )
            } else if (selectedTaskSection === TaskSections.SCHEDULE) {
                const stepData: any = task.steps.map((item: any) => {
                    return { uuid: item.uuid, time_window: item.time_window }
                })
                const data = task.data
                dispatch(
                    updateTaskSchedules({
                        taskUuid: task.uuid,
                        steps: stepData,
                        data,
                    }),
                )
            }
        } else {
            dispatch(
                updateTask({
                    task: task,
                    campaignUuid: uuid,
                    idle_hours_to_submit_engagement,
                    onSuccessCallback: successCallback,
                }),
            )
        }
    }

    const getEditorComponent = (editingState: EditingState): React.ReactElement => {
        switch (editingState) {
            case EditingState.REGISTRATION_PHASE: {
                return <RegistrationEditor selectedTaskSection={selectedTaskSection} />
            }
            case EditingState.EDIT_STEPS: {
                if (tasks.length === 0) {
                    return <EmptyState />
                } else {
                    return selectedTask && selectedTask.type === 'custom' ? (
                        <CustomTaskEditor
                            taskSettings={selectedTaskSetting}
                            task={selectedTask}
                            saveTask={saveTask}
                            removeTask={removeTask}
                            selectedTaskSection={selectedTaskSection}
                            campaignUuid={uuid}
                        />
                    ) : (
                        <PredefinedTaskEditor
                            campaignBrandCountry={campaignBrandCountry}
                            taskSettings={selectedTaskSetting}
                            stepSettings={stepSettings}
                            task={selectedTask as TaskState}
                            saveTask={saveTask}
                            selectedTaskSection={selectedTaskSection}
                            uuid={uuid}
                        />
                    )
                }
            }
            default:
                return campaign && (campaign.registration_enable || tasks.length > 0) ? <></> : <EmptyState />
        }
    }

    useEffect(() => {
        if (campaign?.uuid && campaign?.registration_enable) {
            dispatch(getForm({ uuid: campaign.uuid as string }))
        }

        if (taskUuid) {
            const task = tasks.find(task => task.uuid === taskUuid)
            if (task) {
                selectTask(task, EditingState.EDIT_STEPS)
                if (typeof section === 'string') {
                    setSelectedTaskSection(section as TaskSections)
                }
            }
        } else if (campaign && campaign.registration_enable) {
            selectRegistrationPhase()
            if (typeof section === 'string') {
                setSelectedTaskSection(section as TaskSections)
            }
        } else if (tasks.length > 0) {
            selectTask(tasks[0], EditingState.EDIT_STEPS)
            if (typeof section === 'string') {
                setSelectedTaskSection(section as TaskSections)
            }
        } else {
            setEditingState(EditingState.BLANK)
        }
    }, [campaign, taskUuid, section])

    useEffect(() => {
        if (Boolean(selectedTask) && tasks) {
            setSelectedTask(selected => {
                if (selected) {
                    return tasks.find(t => t.uuid === selected.uuid) || null
                }
                return null
            })
        }
    }, [Boolean(selectedTask), tasks])

    useEffect(() => {
        if (onAddTaskRef.current && tasks.length > 0) {
            const addedTask = tasks[tasks.length - 1]
            selectTask(addedTask, EditingState.EDIT_STEPS)
            setSelectedTaskSection(TaskSections.SETUP)
            updateRoute({ taskUuid: addedTask.uuid })
            onAddTaskRef.current = false
        }
    }, [tasks.length])

    if (campaign?.in_template_flow) return <Redirect to="/404" />

    return (
        <>
            <div className="sidebar">
                <TaskSidebar
                    currentState={editingState}
                    selectRegistrationPhase={selectRegistrationPhase}
                    selectTask={selectTask}
                    createTask={createTask}
                    removeTask={removeTask}
                    selectedTask={selectedTask as TaskState}
                    setSelectedTaskSection={(section: TaskSections): void => {
                        setSelectedTaskSection(section)
                    }}
                    selectedTaskSection={selectedTaskSection}
                />
            </div>
            <div className="task-content">
                <div className="open-small-sidebar">
                    <div className="current-page">{t('campaignEditor.createTask.currentPageMobilePageTitle')}</div>
                    <>
                        {editingState === EditingState.BLANK ? (
                            <RectangleButton
                                form="outlined"
                                size="s"
                                label={t('button.addTask')}
                                icon="plus"
                                onClick={() => {
                                    setShowSidebarTask(true)
                                }}
                                className="width-fit-content"
                            />
                        ) : (
                            <div
                                className="current-page-open-button"
                                onClick={(): void => {
                                    setShowSidebarTask(true)
                                }}
                            >
                                <div>
                                    {selectedTaskSetting?.label
                                        ? selectedTaskSetting.label
                                        : t('taskType.registration')}
                                </div>
                                <Icon.SelectorDownIcon />
                            </div>
                        )}
                    </>
                </div>
                <div className="editor-form" key={selectedTask ? selectedTask.uuid : uuid}>
                    {getEditorComponent(editingState)}
                </div>
            </div>
            {showSidebarTask && (
                <SmallScreenSideBar
                    className={`small-device-sidebar ${showSidebarTask && 'show-small-sidebar'}`}
                    id="small-screen-sidebar-container"
                    onClick={(e: any): void => {
                        if (e.target.id === 'small-screen-sidebar-container') {
                            setShowSidebarTask(false)
                        } else {
                            return
                        }
                    }}
                >
                    <TaskSidebar
                        className="small-screen-sidebar"
                        currentState={editingState}
                        selectRegistrationPhase={selectRegistrationPhase}
                        selectTask={selectTask}
                        createTask={createTask}
                        removeTask={removeTask}
                        onCloseSideBar={(): void => {
                            setShowSidebarTask(false)
                        }}
                        isSmallSideBar
                        selectedTask={selectedTask as TaskState}
                        setSelectedTaskSection={(section: TaskSections): void => {
                            setSelectedTaskSection(section)
                        }}
                        selectedTaskSection={selectedTaskSection}
                    />
                </SmallScreenSideBar>
            )}
        </>
    )
}

export default withSelectedTaskProvider(TaskEditor)
